// Servicio mejorado para manejar la integración con Google Sheets

// Interfaz para los datos de RSVP en Google Sheets
export interface GoogleSheetsRSVPData {
  name: string;
  attendance: string;
  dietary?: string; // Equivalente a allergies
  guestNames?: string; // Utilizado para musicRecommendations
  timestamp: string;
  id?: string;
}

// Variable para controlar intentos fallidos de conexión
let consecutiveFailures = 0;
const MAX_FAILURES = 3;

/**
 * Envía datos de RSVP al Google Script para almacenarlos en Google Sheets
 * Con manejo mejorado de errores y reintentos
 */
export const sendToGoogleSheet = async (data: {
  name: string;
  attendance: string;
  allergies?: string;
  musicRecommendations?: string;
}): Promise<boolean> => {
  try {
    // Obtener la URL del script de Google desde las variables de entorno
    const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL;
    
    if (!scriptUrl) {
      console.error('URL de Google Script no configurada');
      return false;
    }
    
    // Preparar los datos para enviar
    const payload = {
      name: data.name,
      attendance: data.attendance,
      dietary: data.allergies || '',
      guestNames: data.musicRecommendations || '', // Reutilizamos este campo para recomendaciones musicales
      timestamp: new Date().toISOString()
    };
    
    // Enviar los datos al script de Google con timeout de 10 segundos
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const response = await fetch(scriptUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        throw new Error(`Error al enviar datos: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Error desconocido al enviar datos');
      }
      
      // Resetear contador de fallos si hay éxito
      consecutiveFailures = 0;
      return true;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    consecutiveFailures++;
    console.error(`Error al enviar datos a Google Sheets (intento ${consecutiveFailures}):`, error);
    return false;
  }
};

/**
 * Obtiene los datos de RSVP desde Google Sheets con cache inteligente
 */
export const getFromGoogleSheet = async (): Promise<GoogleSheetsRSVPData[] | null> => {
  // Si ha habido demasiados fallos consecutivos, no intentar de nuevo por un tiempo
  if (consecutiveFailures >= MAX_FAILURES) {
    console.warn(`Demasiados intentos fallidos (${consecutiveFailures}). Usando datos locales temporalmente.`);
    
    // Reintentar después de 5 minutos
    setTimeout(() => { consecutiveFailures = 0; }, 5 * 60 * 1000);
    return null;
  }
  
  try {
    // Obtener la URL del script de Google para leer datos
    const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL_READ;
    
    if (!scriptUrl) {
      console.error('URL de Google Script para lectura no configurada');
      return null;
    }
    
    // Construir la URL con el parámetro de acción
    const url = `${scriptUrl}?action=read`;
    
    // Configurar timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      // Obtener los datos del script de Google
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        throw new Error(`Error al obtener datos: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Verificar si los datos son válidos
      if (!Array.isArray(data)) {
        throw new Error('Formato de datos inválido');
      }
      
      // Resetear contador de fallos
      consecutiveFailures = 0;
      
      // Almacenar los datos en localStorage como respaldo (cache)
      try {
        localStorage.setItem('google_sheets_cache', JSON.stringify(data));
        localStorage.setItem('google_sheets_cache_timestamp', new Date().toISOString());
      } catch (cacheError) {
        console.warn('No se pudo cachear los datos:', cacheError);
      }
      
      return data;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    consecutiveFailures++;
    console.error(`Error al obtener datos de Google Sheets (intento ${consecutiveFailures}):`, error);
    
    // Intentar obtener datos del cache si está disponible
    try {
      const cachedData = localStorage.getItem('google_sheets_cache');
      if (cachedData) {
        console.info('Usando datos cacheados de Google Sheets');
        return JSON.parse(cachedData);
      }
    } catch (cacheError) {
      console.warn('Error al leer cache:', cacheError);
    }
    
    return null;
  }
};
